<template>
  <div
    class="card custom-card box-shadow data-load"
    :class="[`custom-card--${tone}`, { 'sk-loading': isLoading }]"
    v-bind="$attrs"
    v-on="$listeners"
  >
    <sk-spinner v-if="isLoading"></sk-spinner>
    <div class="card-body">
      <slot name="body" :value="value" :title="title">
        <div class="custom-card__head">
          <div class="custom-card__icon" aria-hidden="true">
            <i class="fas" :class="icon"></i>
          </div>
          <slot name="title" :value="value" :title="title">
            <h5 class="card-title">{{ title }}</h5>
          </slot>
        </div>

        <slot name="value" :value="value" :title="title">
          <p class="card-text" @click="valueClicked(value)">{{ value }}</p>
        </slot>
      </slot>
    </div>
  </div>
</template>

<script>
import { SkSpinner } from "@/components";

export default {
  name: "CardInfoComponent",
  components: {
    SkSpinner,
  },
  props: {
    title: {
      type: String,
      required: true,
    },
    value: {
      type: [String, Number],
      required: true,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    icon: {
      type: String,
      default: "fa-chart-bar",
    },
    tone: {
      type: String,
      default: "primary",
    },
  },
  methods: {
    valueClicked(value) {
      this.$emit("value-clicked-event", value);
    },
  },
};
</script>

<style lang="scss" scoped>
.custom-card {
  --card-accent: var(--portal-primary);
  --card-soft: var(--portal-primary-soft);
  --card-shadow: rgba(79, 70, 229, 0.35);

  position: relative;
  height: auto;
  min-height: 148px;
  min-width: 0;
  width: 100%;
  overflow: hidden;
  border: 1px solid var(--portal-border) !important;
  border-radius: 16px !important;
  background: var(--portal-surface) !important;
  box-shadow: var(--portal-shadow-sm) !important;
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;

  &::after {
    content: "";
    position: absolute;
    inset: auto 0 0 0;
    height: 3px;
    background: var(--card-accent);
    opacity: 0.9;
  }

  &:hover {
    transform: translateY(-3px);
    border-color: transparent !important;
    box-shadow: var(--portal-shadow) !important;
  }

  &:focus-within {
    outline: 2px solid var(--portal-ring);
    outline-offset: 2px;
  }

  &--primary {
    --card-accent: var(--portal-primary);
    --card-soft: var(--portal-primary-soft);
    --card-shadow: rgba(79, 70, 229, 0.35);
  }

  &--success {
    --card-accent: var(--portal-success);
    --card-soft: var(--portal-success-soft);
    --card-shadow: rgba(5, 150, 105, 0.35);
  }

  &--warning {
    --card-accent: var(--portal-warning);
    --card-soft: var(--portal-warning-soft);
    --card-shadow: rgba(217, 119, 6, 0.35);
  }

  &--danger {
    --card-accent: var(--portal-danger);
    --card-soft: var(--portal-danger-soft);
    --card-shadow: rgba(220, 38, 38, 0.35);
  }

  &--info {
    --card-accent: var(--portal-info);
    --card-soft: var(--portal-info-soft);
    --card-shadow: rgba(2, 132, 199, 0.35);
  }

  &--teal {
    --card-accent: var(--portal-secondary);
    --card-soft: rgba(13, 148, 136, 0.12);
    --card-shadow: rgba(13, 148, 136, 0.35);
  }

  & > .card-body {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    gap: 1.1rem;
    min-height: 148px;
    padding: 1.25rem 1.25rem 1.2rem;
  }
}

.custom-card__head {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
}

.custom-card__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  flex-shrink: 0;
  border-radius: 12px;
  background: var(--card-accent);
  color: #fff;
  font-size: 1rem;
  box-shadow: 0 6px 14px var(--card-shadow);
}

.custom-card--primary .custom-card__icon {
  background: var(--portal-gradient-primary);
}

.custom-card > .card-body .card-title {
  margin: 0;
  padding-top: 0.2rem;
  color: var(--portal-text-muted);
  font-size: 0.84rem;
  font-weight: 600;
  letter-spacing: -0.01em;
  line-height: 1.35;
  text-transform: none;
}

.custom-card .card-text {
  margin: 0;
  color: var(--portal-text);
  font-size: 2.35rem;
  font-weight: 700;
  letter-spacing: -0.04em;
  line-height: 1;
}
</style>
